import { useCallback, useEffect, useMemo, useState } from "react";

import { groupMenuItems } from "@/components/groups/groupMenu";
import { useGroups } from "@/components/groups/useGroups";
import { useDialogs } from "@/components/shell/Dialogs";
import { addTags, setFlag, setSuspended } from "@/lib/cards";
import { drillCards, type ReviewCard, type ReviewPosition } from "@/lib/review";
import { Session } from "@/surfaces/review/Session";
import {
  imageModeOptions,
  loadImageMode,
  saveImageMode,
  type ImageMode,
} from "@/surfaces/review/sessionRules";

import {
  attemptOf,
  canUndo,
  currentCard,
  firstTimePasses,
  grade,
  isFinished,
  missedCards,
  pendingRepeats,
  startDrill,
  totalMisses,
  undo,
  type DrillState,
} from "./drillQueue";

/**
 * A drill (日本-601, frames D and E): the chosen groups' cards on the study screen, graded here and
 * **written nowhere**.
 *
 * `Session` is the sitting's own component — same card face, keys, swipes and `Card ⋯` — driven by a
 * position built in this file rather than by the server's. `answer` and `undo` never reach an API;
 * the card menu's flag, suspend, tag and edit still act on the real card, because those are things
 * you do to a card rather than answers about it.
 */

/**
 * The run in progress, kept across an unmount so `ノートを編集` and Back return to it.
 *
 * **A module variable, not storage, and that is the whole rule "each drill is its own run"
 * expressed as a lifetime**: it survives leaving for 単語 and coming back, and it does not survive
 * a reload — which starts the run over on the same groups, as the URL says.
 */
let saved: { key: string; cards: Map<number, ReviewCard>; state: DrillState } | null = null;

const EMPTY_DAY = { cardsDone: 0, cardsTotal: 0, passes: 0, fails: 0, reviewsTotal: 0 };

export function Drill({
  groupIds,
  isPhone,
  onEnd,
  onOpenNote,
}: {
  groupIds: string[];
  isPhone: boolean;
  onEnd: () => void;
  onOpenNote: (noteId: number) => void;
}) {
  const dialogs = useDialogs();
  const key = groupIds.join(",");
  const [cards, setCards] = useState<Map<number, ReviewCard> | null>(
    saved?.key === key ? saved.cards : null,
  );
  const [state, setState] = useState<DrillState | null>(saved?.key === key ? saved.state : null);
  // Membership for the card on screen, so the グループに追加 submenu ticks the groups it is already
  // in — without it every group reads unticked and choosing one can only ever add.
  const headNoteId = state && cards ? cards.get(currentCard(state) ?? -1)?.noteId : undefined;
  const groupState = useGroups(headNoteId === undefined ? [] : [headNoteId]);
  /** A card-menu act that failed. A notice, not `error`: the run must survive a failed flag. */
  const [notice, setNotice] = useState<string | null>(null);
  const [autoplay, setAutoplay] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [imageMode, setImageMode] = useState<ImageMode>(() =>
    loadImageMode(typeof window === "undefined" ? null : window.localStorage),
  );

  const chosen = useMemo(
    () => groupState.groups.filter((g) => groupIds.includes(g.id)),
    [groupState.groups, groupIds],
  );
  const title = chosen.map((g) => g.name).join(" ＋ ");

  /** Start (or restart) a run over these notes — the groups' union, or `不可だったN枚だけ`. */
  const begin = useCallback(
    async (noteIds: number[]) => {
      setError(null);
      try {
        const loaded = await drillCards(noteIds);
        const byId = new Map(loaded.cards.map((c) => [c.cardId, c]));
        setAutoplay(loaded.autoplayWord);
        setCards(byId);
        setState(startDrill([...byId.keys()]));
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    },
    [],
  );

  // First load: once the groups are known, drill the union of their notes — unless the run this
  // tab left a moment ago is the one being returned to.
  useEffect(() => {
    if (state !== null || !groupState.loaded) return;
    if (chosen.length === 0) {
      setError("選んだグループが見つかりません。");
      return;
    }
    const noteIds = [...new Set(chosen.flatMap((g) => g.noteIds))];
    if (noteIds.length === 0) {
      setError("選んだグループにカードがありません。");
      return;
    }
    void begin(noteIds);
  }, [begin, chosen, groupState.loaded, state]);

  useEffect(() => {
    if (cards && state) saved = { key, cards, state };
  }, [cards, key, state]);

  const leave = useCallback(() => {
    saved = null;
    onEnd();
  }, [onEnd]);

  const refreshCard = useCallback(async (card: ReviewCard) => {
    const fresh = await drillCards([card.noteId]);
    setCards((current) => {
      if (!current) return current;
      const next = new Map(current);
      for (const c of fresh.cards) if (next.has(c.cardId)) next.set(c.cardId, c);
      return next;
    });
  }, []);

  const act = useCallback(
    async (fn: () => Promise<unknown>, card: ReviewCard) => {
      try {
        setNotice(null);
        await fn();
        await refreshCard(card);
      } catch (e) {
        setNotice(e instanceof Error ? e.message : String(e));
      }
    },
    [refreshCard],
  );

  if (error) {
    return (
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-4 p-6 text-center">
        <p className="text-secondary text-ink-2">{error}</p>
        <button
          type="button"
          onClick={leave}
          className="rounded-[var(--r-2)] border border-line-strong px-3 py-2 text-secondary font-semibold text-ink-2"
        >
          混同に戻る
        </button>
      </div>
    );
  }

  if (!cards || !state) {
    return <div className="flex-1 p-6 text-secondary text-ink-3">…</div>;
  }

  if (isFinished(state)) {
    return (
      <DrillFinished
        title={title}
        state={state}
        cards={cards}
        isPhone={isPhone}
        onAgain={() => void begin([...new Set([...cards.values()].map((c) => c.noteId))])}
        onMissedOnly={(ids) =>
          void begin([...new Set(ids.map((id) => cards.get(id)?.noteId ?? 0).filter(Boolean))])
        }
        onDone={leave}
      />
    );
  }

  const headId = currentCard(state);
  const headCard = headId === null ? null : (cards.get(headId) ?? null);
  // `reps` is what `Session` keys its reset on besides the id, and a drill never moves the real
  // one — so a missed LAST card, which comes straight back as the same card, would stay revealed.
  // The run's own grade count moves on every grade and every undo.
  const card = headCard ? { ...headCard, reps: state.history.length } : null;
  const position: ReviewPosition = {
    deck: title,
    idleMinutes: 0,
    autoplayWord: autoplay,
    card,
    counts: { new: 0, learning: 0, review: 0, answered: state.passed.length, remaining: state.queue.length },
    canUndo: canUndo(state),
    day: EMPTY_DAY,
  };

  return (
    <Session
      position={position}
      isPhone={isPhone}
      imageMode={imageMode}
      imageModes={imageModeOptions()}
      busy={false}
      notice={notice}
      onImageMode={(mode) => {
        setImageMode(mode);
        saveImageMode(typeof window === "undefined" ? null : window.localStorage, mode);
      }}
      onAnswer={(_card, passed) => setState((s) => (s ? grade(s, passed) : s))}
      onUndo={() => setState((s) => (s ? undo(s) : s))}
      onEnd={leave}
      onEdit={(c) => onOpenNote(c.noteId)}
      onInfo={(c) => onOpenNote(c.noteId)}
      onSuspend={(card, wholeNote) =>
        void (async () => {
          if (wholeNote) {
            const ok = await dialogs.confirm({
              title: "ノート全体を停止しますか？",
              body: `${card.headword} から作られたカードはすべて、停止を解除するまでキューから外れます。`,
              confirmLabel: "ノートを停止",
              tone: "danger",
            });
            if (!ok) return;
          }
          await act(() => setSuspended(wholeNote ? card.noteCardIds : [card.cardId], true), card);
        })()
      }
      onFlag={(c, value) => void act(() => setFlag([c.cardId], value), c)}
      onAddTag={(c) =>
        void (async () => {
          const value = await dialogs.prompt({
            title: "タグを追加",
            label: c.headword,
            hint: "スペース区切りで複数指定できます。",
            submitLabel: "追加",
          });
          if (value?.trim()) await act(() => addTags([c.noteId], value.trim()), c);
        })()
      }
      groupItems={(c) =>
        groupMenuItems(groupState.groups, [c.noteId], groupState.membership, groupState.actions)
      }
      drill={{
        title,
        passed: state.passed.length,
        total: state.all.length,
        repeats: pendingRepeats(state),
        attempt: headId === null ? 1 : attemptOf(state, headId),
      }}
    />
  );
}

/** `7e`'s counterpart for a drill (frame E). Thrown away on leaving — nothing is recorded. */
function DrillFinished({
  title,
  state,
  cards,
  isPhone,
  onAgain,
  onMissedOnly,
  onDone,
}: {
  title: string;
  state: DrillState;
  cards: Map<number, ReviewCard>;
  isPhone: boolean;
  onAgain: () => void;
  onMissedOnly: (cardIds: number[]) => void;
  onDone: () => void;
}) {
  const missed = missedCards(state);
  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto" data-check="drill.finished">
      <div
        className={
          isPhone
            ? "flex flex-col gap-5 px-[18px] py-6"
            : "mx-auto flex w-full max-w-[640px] flex-col gap-6 px-5 py-10"
        }
      >
        <div className="flex flex-col gap-1">
          <h2 className="text-[24px]/[1.3] font-semibold text-ink">ドリル終了</h2>
          <span className="text-secondary text-ink-4">{title}</span>
        </div>
        <div className="flex gap-9">
          <Figure value={state.all.length} label="枚" />
          <Figure value={firstTimePasses(state)} label="一発合格" />
          <Figure value={totalMisses(state)} label="不可の回数" red={totalMisses(state) > 0} />
        </div>
        {missed.length > 0 && (
          <ul className="rounded-[var(--r-3)] border border-line-strong bg-panel" data-check="drill.missed">
            {missed.map((id) => {
              const card = cards.get(id);
              return (
                <li
                  key={id}
                  className="flex items-baseline gap-3.5 border-b border-line-soft px-4 py-[9px] last:border-b-0"
                >
                  <span className="min-w-[5em] font-jp text-[17px] font-medium text-ink">
                    {card?.headword ?? ""}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-meta text-ink-3">
                    {(card?.gloss ?? "").replace(/\s+/g, " ")}
                  </span>
                  <span className="font-mono text-meta text-accent-text">×{state.misses[id]}</span>
                </li>
              );
            })}
          </ul>
        )}
        <div className="flex flex-wrap gap-2.5">
          <button
            type="button"
            onClick={onAgain}
            data-check="drill.again"
            className="rounded-[var(--r-2)] bg-accent px-[13px] py-2 text-secondary font-semibold text-white"
          >
            もう一度
          </button>
          {missed.length > 0 && (
            <button
              type="button"
              onClick={() => onMissedOnly(missed)}
              data-check="drill.missed-only"
              className="rounded-[var(--r-2)] border border-line-strong px-[13px] py-2 text-secondary font-semibold text-ink-2"
            >
              不可だった{missed.length}枚だけ
            </button>
          )}
          <button
            type="button"
            onClick={onDone}
            data-check="drill.done"
            className="rounded-[var(--r-2)] px-[13px] py-2 text-secondary font-semibold text-ink-3"
          >
            混同に戻る
          </button>
        </div>
      </div>
    </div>
  );
}

function Figure({ value, label, red = false }: { value: number; label: string; red?: boolean }) {
  return (
    <div className="flex flex-col gap-1">
      <span
        className={`text-[40px]/[1] font-semibold tabular-nums ${red ? "text-accent-text" : "text-ink"}`}
      >
        {value}
      </span>
      <span className="text-meta text-ink-4">{label}</span>
    </div>
  );
}
